/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - Таблицы</h2>
    <p className={styles.description}>
      Если контент выглядит как пара "ключ: значение", то это причина использовать таблицы. Таблицы позволяют отобразить
      очень сложные связи, размещая в ячейках любой контент. Они имеют свои правила CSS для оформления внешнего вида,
      отличающиеся от правил для обычных элементов.
    </p>
    <p className={styles.description}>
      Неудивительно, что много лет назад таблицы использовались для верстки страницы целиком. Эту практику
      последовательно преследуют все поисковые сервисы, поддерживая рейтинг сайтов с семантической разметкой и понижая
      рейтинг сайтам с табличной версткой. Однако существую ситуации, когда такая верстка уместна - создание электронных
      писем и верстка сайтов под IE6 (в основном для Китая).
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Синтаксис</h3>
      <p className={styles.description}>
        Таблица начинается и заканчивается тегами <code className={styles.code}>table</code>. Затем следует первый
        потомок тега table - тег <code className={styles.code}>tbody</code> Как правило это тег не вносят в html код,
        т.к. браузер автоматически подставит его в любом случае. Далее может располагаться необязательное описание всей
        таблицы <code className={styles.code}>caption</code>. <code className={styles.code}>tfoot</code> обозначает
        группу строк таблицы в области нижнего колонтитула, а <code className={styles.code}>thead</code> обозначает
        группу строк таблицы в области заголовка.
      </p>
      <p className={styles.description}>
        Минимальная таблица состоит из одного ряда - тега <code className={styles.code}>tr</code>. Ряд не должен быть
        пустым, т.е. в ряду должна быть хотя бы одна ячейка - тег <code className={styles.code}>td</code> (или{' '}
        <code className={styles.code}>th</code> - ячейка-заголовок). В отношении рядов и ячеек действуют такие правила:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          число ячеек в каждом столбце должно быть одинаково с числом ячеек в других столбцами таблицы{' '}
        </li>
        <li className={styles.list__item}>
          число ячеек в каждом ряду должно быть одинаково с числом ячеек в других рядах таблицы{' '}
        </li>
      </ul>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_3_1"
        src="https://codepen.io/rotmanyan/embed/KvvJVy?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <p className={styles.description}>
        Правило равенства числа ячеек в рядах и столбцах нельзя отменить, но можно обойти, используя слияние ячеек в
        колонках с помощью атрибута <code className={styles.code}>colspan</code> и используя слияние ячеек в рядах с
        помощью атрибута <code className={styles.code}>rowspan</code>. Визуально правило равенства числа ячеек будет
        нарушено, но браузер учитывает ячейки в объединенных группах так, как если бы групп не было.
      </p>
      <p className={styles.description}>
        Использовать правила colspan и rowspan несложно. Нужно определить группу ячеек для слияния и затем в самой
        верхней и левой ячейке группы задать атрибут с числом ячеек требующих объединения справа /colspan/ и снизу
        /rowspan/. После этого обязательно нужно удалить ставшие ненужными ячейки справа и снизу в количестве на единицу
        меньше значения colspan или rowspan.{' '}
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_3_1"
        src="https://codepen.io/rotmanyan/embed/OjjdXb?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - CSS - свойства для таблиц</h3>
      <p className={styles.description}>
        Таблицы имеют особые правила оформления. Определены две совершенно разные модели border зависящие от значения
        свойства <code className={styles.code}>border-collapse</code>{' '}
      </p>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>border-collapse:</th>
            <th>правила</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>separate</td>
            <td>
              {' '}
              <ul className={styles.list}>
                <li className={styles.list__item}>каждая ячейка таблицы отстоит от других на некотором расстоянии </li>
                <li className={styles.list__item}>рамки ячеек не сливаются друг с другом </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td>collapse</td>
            <td>
              <ul className={styles.list}>
                <li className={styles.list__item}>
                  между бордерами вокруг таблицы и ее внешними ячейками никогда не может быть пробела{' '}
                </li>
                <li className={styles.list__item}>при слиянии рамки между ячейками центрируются </li>
                <li className={styles.list__item}>
                  половина ширины рамки таблицы включена в ширину таблицы /* важно для верстки с пиксельной точностью */{' '}
                </li>
                <li className={styles.list__item}>две или более рамки сливаются: </li>
              </ul>
            </td>
          </tr>
        </tbody>
      </table>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_3_1"
        src="https://codepen.io/rotmanyan/embed/zddeoy?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
            href="https://developer.mozilla.org/en-US/docs/Learn/CSS/Building_blocks/Styling_tables"
          >
            MDN: Стили CSS для таблиц
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.tablesgenerator.com/html_tables"
          >
            Пример генератора таблиц
          </a>
        </li>
        <li className={styles.list__item}>
          <a className={styles.link} target="_blank" rel="noopener noreferrer" href="https://validator.w3.org/">
            Валидатор HTML
          </a>
        </li>
      </ul>
    </section>
  </section>
))
